import * as React from 'react';
import InnerImageZoom from '../InnerImageZoom/index'
import '../InnerImageZoom/styles.css'
import '../styles.css';

const ESCAPE_KEY = 27;
const RIGHT_KEY = 39;
const LEFT_KEY = 37;
const MOBILE_BREAKPOINT = 768;

function isMobileViewport() {
  return window.innerWidth <= MOBILE_BREAKPOINT;
}

function isLocalVideo(resource) {
  return Boolean(resource?.url && (resource.url.includes('.mp4') || resource.url.includes('.mov')));
}

export default function ReactImageVideoLightbox({
  data,
  startIndex,
  showResourceCount,
  onCloseCallback,
  onNavigationCallback,
}) {
  const [index, setIndex] = React.useState(startIndex);
  const [loading, setLoading] = React.useState(true);
  const previousOverflowRef = React.useRef();

  React.useEffect(() => {
    setIndex(startIndex);
    setLoading(true);
  }, [startIndex]);

  const handleNavigation = React.useMemo(
    () => (typeof onNavigationCallback === 'function' ? onNavigationCallback : () => {}),
    [onNavigationCallback]
  );

  const closeViewer = React.useCallback(() => {
    onCloseCallback();
  }, [onCloseCallback]);

  React.useEffect(() => {
    previousOverflowRef.current = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousOverflowRef.current || null;
    };
  }, []);

  const goToPrevious = React.useCallback(() => {
    setIndex((currentIndex) => {
      if (currentIndex <= 0) {
        return currentIndex;
      }

      const nextIndex = currentIndex - 1;
      setLoading(true);
      handleNavigation(nextIndex);
      return nextIndex;
    });
  }, [handleNavigation]);

  const goToNext = React.useCallback(() => {
    setIndex((currentIndex) => {
      if (currentIndex >= data.length - 1) {
        return currentIndex;
      }

      const nextIndex = currentIndex + 1;
      setLoading(true);
      handleNavigation(nextIndex);
      return nextIndex;
    });
  }, [data.length, handleNavigation]);

  React.useEffect(() => {
    function handleKeyDown(event) {
      switch (event.keyCode) {
        case ESCAPE_KEY:
          closeViewer();
          break;
        case RIGHT_KEY:
          goToNext();
          break;
        case LEFT_KEY:
          goToPrevious();
          break;
        default:
          break;
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [closeViewer, goToPrevious, goToNext]);

  function openNativeVideo(url) {
    window.location.assign(url);
  }

  function handleMediaLoaded() {
    setLoading(false);
  }

  function renderPhoto(resource, ratio) {
    if (isMobileViewport()) {
      return (
        <img
          key={index}
          className="img-viewer viewer-media viewer-media-image"
          alt={resource.altTag}
          src={resource.url}
          onLoad={handleMediaLoaded}
        />
      );
    }

    return (
      <InnerImageZoom
        src={resource.url}
        width={ratio * window.innerWidth}
        hasSpacer={false}
        onLoad={handleMediaLoaded}
        key={index}
      />
    );
  }

  function renderMobileVideoPoster(resource) {
    return (
      <button
        key={index}
        type="button"
        onClick={() => openNativeVideo(resource.url)}
        className="viewer-mobile-video-button"
      >
        <img
          src={resource.poster || resource.url}
          alt={resource.title || 'Video preview'}
          className="viewer-mobile-video-poster"
          onLoad={handleMediaLoaded}
        />
      </button>
    );
  }

  function renderLocalVideo(resource) {
    return (
      <video
        onLoadStart={handleMediaLoaded}
        onLoadedData={handleMediaLoaded}
        key={index}
        className="video-viewer"
        preload="metadata"
        poster={resource.poster ? resource.poster : null}
        playsInline
        controls
        autoPlay={false}
        src={resource.url}
      >
        <source src={resource.url + "#t=0.1"} type="video/webm" />
        <source src={resource.url + "#t=0.1"} type="video/ogg" />
        <source src={resource.url + "#t=0.1"} type="video/mp4" />
        <source src={resource.url + "#t=0.1"} type="video/3gp" />
      </video>
    );
  }

  function renderEmbeddedVideo(resource) {
    return (
      <iframe
        key={index}
        width="560"
        height="315"
        src={resource.url}
        frameBorder="0"
        allow="autoplay; encrypted-media"
        title={resource.title}
        allowFullScreen
        className="viewer-media"
        onLoad={handleMediaLoaded}
      ></iframe>
    );
  }

  function renderResource() {
    const resource = data[index];
    if (!resource) {
      return null;
    }

    const ratio = window.innerHeight / window.innerWidth * resource.width / resource.height;

    if (resource.type === 'photo') {
      return renderPhoto(resource, ratio);
    }

    if (resource.type === 'video') {
      if (isMobileViewport() && isLocalVideo(resource)) {
        return renderMobileVideoPoster(resource);
      }

      if (isLocalVideo(resource)) {
        return renderLocalVideo(resource);
      }

      return renderEmbeddedVideo(resource);
    }

    return null;
  }

  const currentResource = renderResource();

  return (
    <div className="viewer-overlay">
      {showResourceCount && (
        <div className="viewer-count">
          <span>{index + 1}</span> / <span>{data.length}</span>
        </div>
      )}

      <div className="viewer-close" onClick={closeViewer}>
        <svg xmlns="http://www.w3.org/2000/svg" height="36px" viewBox="0 0 24 24" width="36px" fill="#FFFFFF">
          <path d="M0 0h24v24H0z" fill="none" />
          <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
        </svg>
      </div>

      {index + 1 !== 1 ? (
        <div className="viewer-arrow viewer-arrow-left" onClick={goToPrevious}>
          <svg xmlns="http://www.w3.org/2000/svg" height="48px" viewBox="0 0 24 24" width="48px" fill="#FFFFFF">
            <path d="M0 0h24v24H0z" fill="none" />
            <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
          </svg>
        </div>
      ) : null}

      {index + 1 !== data.length ? (
        <div className="viewer-arrow viewer-arrow-right" onClick={goToNext}>
          <svg xmlns="http://www.w3.org/2000/svg" height="48px" viewBox="0 0 24 24" width="48px" fill="#FFFFFF">
            <path d="M0 0h24v24H0z" fill="none" />
            <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" />
          </svg>
        </div>
      ) : null}

      {loading && (
        <div className="viewer-spinner-wrap">
          <div className="viewer-spinner"></div>
        </div>
      )}

      {currentResource}
    </div>
  );
}
